import React, { type MouseEvent, type ReactNode, useEffect, useState } from 'react';

import classnames from 'classnames';

import { Key } from '../../utils';
import { Button } from '../Button/Button';
import { Icon } from '../Icon';
import { Popover, type PopoverProps } from '../Popover/Popover';
import { Text } from '../Typography';

import styles from './Popconfirm.module.scss';

export type PopconfirmProps = {
    /**
     * The text of the Cancel button
     */
    cancelText: ReactNode;
    /**
     * Children elements inside a component
     */
    children: ReactNode;
    /**
     * The additional css class
     */
    className?: string;
    /**
     * The text of the Confirm button
     */
    okText: ReactNode;
    /**
     * A callback of cancel
     */
    onCancel?: (e?: MouseEvent<HTMLElement>) => void;
    /**
     * A callback of confirm
     */
    onConfirm?: (e?: MouseEvent<HTMLElement>) => void;
    /**
     * Callback executed when visibility of the card is changed
     */
    onVisibleChange?: (visible: boolean, e?: MouseEvent<HTMLElement> | KeyboardEvent) => void;
    /**
     * The position of the tooltip relative to the target
     */
    placement?: PopoverProps['placement'];
    /**
     * The text shown
     */
    title: ReactNode;
    /**
     * Whether the floating card is open or not
     */
    visible?: boolean;
};

export const Popconfirm = (props: PopconfirmProps) => {
    const {
        cancelText,
        children,
        className,
        okText,
        onCancel,
        onConfirm,
        onVisibleChange,
        title,
        placement,
        visible
    } = props;

    const [internalVisible, setInternalVisible] = useState(false);
    const isVisibilityControlled = 'visible' in props;

    const setVisible = (value: boolean, e?: MouseEvent<HTMLButtonElement> | KeyboardEvent) => {
        onVisibleChange?.(value, e);
        setInternalVisible(value);
    };

    const handleConfirm = (e: MouseEvent<HTMLButtonElement>) => {
        if (!isVisibilityControlled) {
            setVisible(false, e);
        }
        onConfirm?.(e);
    };

    const handleCancel = (e: MouseEvent<HTMLButtonElement>) => {
        setVisible(false, e);
        onCancel?.(e);
    };

    const handleKeyUp = (e: KeyboardEvent) => {
        if (e.key === Key.Escape) {
            setVisible(false, e);
        }
    };

    const handleVisibleChange = (value: boolean) => {
        if (!isVisibilityControlled) {
            setVisible(value);
        }
    };

    useEffect(() => {
        if (isVisibilityControlled) {
            setInternalVisible(!!visible);
        }
    }, [visible, isVisibilityControlled]);

    useEffect(() => {
        window.addEventListener('keyup', handleKeyUp);
        return () => {
            window.removeEventListener('keyup', handleKeyUp);
        };
    }, []);

    const renderContent = () => {
        return (
            <div>
                <div className={styles.content}>
                    <Icon name="s-icon-circle-exclamation" color="warning" size="large" />
                    <div className={styles.title}>
                        <Text type="text">{title}</Text>
                    </div>
                </div>
                <div className={styles.buttons}>
                    {cancelText && (
                        <Button
                            type="outlined"
                            onClick={handleCancel}
                            size="medium"
                            color="neutral"
                        >
                            {cancelText}
                        </Button>
                    )}
                    {okText && (
                        <Button onClick={handleConfirm} size="medium" type="outlined">
                            {okText}
                        </Button>
                    )}
                </div>
            </div>
        );
    };

    return (
        <Popover
            className={classnames(styles.popconfirm, className)}
            content={renderContent()}
            onOpenChange={handleVisibleChange}
            open={internalVisible}
            placement={placement}
            trigger="click"
        >
            {children}
        </Popover>
    );
};

Popconfirm.displayName = 'Popconfirm';
